import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TOURateType } from 'clipsal-cortex-types/src/api';
import { DialogProps } from 'clipsal-cortex-types/src/common/chakra-extension-types';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { Dialog } from '../../../../../../common/components/Dialog';
import { MAX_DOLLARS_PER_KWH_VALUE } from '../../../constants';
import { API_RATE_TYPE_TO_FORM_RATE_TYPE } from '../../../form-mapping-utils';
import { useTariffFormContext } from '../../../tariff-form-context';
import { SolarFeedInFormData } from './types';

const solarFeedInFormSchema = yup.object().shape({
  rates: yup.array().of(
    yup.object().shape({
      rateId: yup.number().nullable(),
      rate: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .min(0)
        .max(MAX_DOLLARS_PER_KWH_VALUE, `Must be less than $${MAX_DOLLARS_PER_KWH_VALUE}`)
        .typeError('This field is required')
        .required(),
      name: yup.string().nullable(),
      type: yup.string().nullable(),
    })
  ),
});

const DEFAULT_FORM_VALUES = {
  rates: [{ id: null, rate: null }],
};

export function SolarFeedInFormDialog({ isOpen, onClose }: DialogProps) {
  const {
    formData: {
      additionalRatesAndDiscounts: { solarFeedIn, rebates },
    },
    onUpdateFormData,
  } = useTariffFormContext();
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<SolarFeedInFormData>({
    resolver: yupResolver(solarFeedInFormSchema),
    defaultValues: solarFeedIn?.rates.length ? solarFeedIn : DEFAULT_FORM_VALUES,
  });

  const { fields: feedInRates } = useFieldArray({
    control,
    name: 'rates',
  });

  const hasMoreThanOneRate = feedInRates.length > 1;

  const { isOpen: isAlertDialogOpen, onClose: onCloseAlertDialog, onOpen: onOpenAlertDialog } = useDisclosure();

  async function handleSubmitSolarFeedInForm(solarFeedInFormData: SolarFeedInFormData) {
    onUpdateFormData((p) => ({
      ...p,
      additionalRatesAndDiscounts: { ...p.additionalRatesAndDiscounts, solarFeedIn: solarFeedInFormData },
    }));
    onClose();
  }

  function handleClearForm() {
    reset(DEFAULT_FORM_VALUES);
    onUpdateFormData((p) => ({
      ...p,
      additionalRatesAndDiscounts: { ...p.additionalRatesAndDiscounts, solarFeedIn: null },
    }));
    onClose();
  }

  return (
    <>
      <Dialog showCloseButton title="Solar Feed-in Rates" isOpen={isOpen} onClose={onClose}>
        <Box
          data-testid="solar-feed-in-rate-form"
          mx={4}
          as="form"
          onSubmit={handleSubmit(handleSubmitSolarFeedInForm)}
        >
          {/* For now we do not allow changing export rates when there is more than one rate */}
          {hasMoreThanOneRate && (
            <Box mb={4}>
              <Alert status="info" variant="left-accent">
                <AlertIcon />
                These TOU rates have been automatically configured and they cannot be updated here. Please contact us if
                you need help.
              </Alert>
            </Box>
          )}

          <Box mt={2}>
            {feedInRates.map((rateField, rateIndex) => {
              const error = errors?.rates?.[rateIndex];
              const rateType = rateField.type
                ? API_RATE_TYPE_TO_FORM_RATE_TYPE[rateField.type as Exclude<TOURateType, 'SINGLE'>]
                : '';
              return (
                <FormControl key={rateField.id} mb={4} isInvalid={!!error?.rate} isDisabled={hasMoreThanOneRate}>
                  <FormLabel mb={0} mr={0} fontWeight="bold">
                    Solar Feed-in Rate {hasMoreThanOneRate ? rateIndex + 1 : ''} {rateField.type ? `(${rateType})` : ''}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      data-testid={`solar-feed-in-rate-input-${rateIndex}`}
                      {...register(`rates.${rateIndex}.rate`)}
                      type={'number'}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      step="0.0000000001"
                    />
                    <InputRightAddon>$ / kWh</InputRightAddon>
                  </InputGroup>
                  <FormErrorMessage data-testid={`solar-feed-in-rate-error-message-${rateIndex}`}>
                    {error?.rate?.message}
                  </FormErrorMessage>
                </FormControl>
              );
            })}

            {/* For now display rebates here but cannot be edited */}
            {!!rebates?.rates.length &&
              rebates.rates.map((rebate, rebateIndex) => (
                <FormControl mb={4} key={rebate.id} isDisabled>
                  <FormLabel mb={0} mr={0} fontWeight="bold">
                    Solar Feed-in Rate {feedInRates.length + rebateIndex + 1} (REBATE)
                  </FormLabel>
                  <InputGroup>
                    <Input value={rebates.rates[rebateIndex].rate} isDisabled />
                    <InputRightAddon>$ / kWh</InputRightAddon>
                  </InputGroup>
                </FormControl>
              ))}
          </Box>

          <Center flexDirection="column" mt={6}>
            <Button
              data-testid="submit-solar-feed-in-form-btn"
              type="submit"
              w={['75%', '75%', '50%']}
              rounded={20}
              colorScheme="dusk100"
              isDisabled={hasMoreThanOneRate}
            >
              Save
            </Button>

            <Button
              data-testid="clear-solar-feed-in-btn"
              onClick={onOpenAlertDialog}
              mt={2}
              variant="ghost"
              w={['75%', '75%', '50%']}
              colorScheme={'red'}
              isDisabled={hasMoreThanOneRate}
            >
              Clear All
            </Button>
          </Center>
        </Box>
      </Dialog>
      <AlertDialogModal
        header="Are you sure you want to clear your solar feed in rate?"
        subHeader=" "
        isOpen={isAlertDialogOpen}
        onClose={onCloseAlertDialog}
        confirmButtonTextColor={'customRed.500'}
        onConfirm={handleClearForm}
        confirmButtonName="Clear"
      />
    </>
  );
}
